import React from 'react';
import { motion } from 'framer-motion';
import { DatabaseIcon, SettingsIcon, BuildingIcon, MessageCircleIcon, CalendarIcon, MapPinIcon, PhoneIcon, MailIcon, LinkedinIcon, InstagramIcon, FacebookIcon, ChevronRightIcon } from 'lucide-react';
const footerLinks = {
  coreprint: {
    title: 'CorePrint',
    links: [{
      label: 'Wat is CorePrint?',
      href: 'https://coreprint.nl/'
    }, {
      label: 'Hoe het werkt',
      href: 'https://coreprint.nl/#hoe'
    }, {
      label: 'Voor wie',
      href: 'https://coreprint.nl/#voor-wie'
    }, {
      label: 'Bewijs & resultaten',
      href: 'https://coreprint.nl/#bewijs'
    }, {
      label: 'Plan een presentatie',
      href: 'https://coreprint.nl/#gesprek'
    }]
  },
  diensten: {
    title: 'Diensten',
    links: [{
      label: 'SEO',
      href: '/seo'
    }, {
      label: 'Google Ads',
      href: '/#diensten'
    }, {
      label: 'Strategie & Advies',
      href: '/#diensten'
    }, {
      label: 'Hosting & Beheer',
      href: '/hosting'
    }, {
      label: 'E-mail Marketing',
      href: '/email'
    }],
    subtle: {
      label: 'Webdesign Alkmaar',
      href: 'https://webdesignalkmaar.nl/'
    }
  },
  noordDigital: {
    title: 'Noord Digital',
    links: [{
      label: 'Over ons',
      href: '/over-ons'
    }, {
      label: 'Ons team',
      href: '/over-ons#team'
    }, {
      label: 'Cases',
      href: '/#cases'
    }, {
      label: 'Blog',
      href: '/bibliotheek'
    }, {
      label: 'Vacatures',
      href: '/sollicitatie'
    }]
  },
  contact: {
    title: 'Contact',
    links: [{
      label: 'Neem contact op',
      href: '/#contact'
    }, {
      label: 'Kennismaking plannen',
      href: '/#contact'
    }, {
      label: 'Schuit 8a, 1724 BD Oudkarspel',
      href: 'https://maps.google.com/?q=Schuit+8a+1724+BD+Oudkarspel'
    }, {
      label: '06 53689686',
      href: 'tel:+31653689686'
    }, {
      label: 'info@noorddigital.nl',
      href: 'mailto:info@noorddigital.nl'
    }]
  }
};
const socialLinks = [{
  icon: LinkedinIcon,
  href: 'https://www.linkedin.com/company/noord-digital/',
  label: 'LinkedIn'
}, {
  icon: FacebookIcon,
  href: 'https://www.facebook.com/profile.php?id=61572493568612',
  label: 'Facebook'
}];
const fadeUp = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  }
};
export function Footer() {
  return <footer className="w-full bg-white relative pt-56 mt-16" data-id="element-275">
      {/* Top Curved Banner */}
      <div className="absolute top-0 left-0 w-full -mt-8 px-4 md:px-6 lg:px-8 z-20" data-id="element-276">
        <div className="container mx-auto max-w-[1200px]" data-id="element-277">
          <div className="relative rounded-[2.5rem] overflow-hidden bg-[#007AFF] shadow-2xl flex flex-col md:flex-row items-stretch min-h-[320px]" data-id="element-278">
            {/* Blue Background with Curve */}
            <div className="absolute inset-0 bg-[#0066CC] z-0" style={{
            clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 85%)'
          }} data-id="element-279"></div>
            <div className="absolute inset-0 bg-[#007AFF] z-0" style={{
            clipPath: 'polygon(0 0, 80% 0, 60% 100%, 0 100%)'
          }} data-id="element-280"></div>

            {/* Content Left */}
            <div className="relative z-10 p-10 md:p-16 md:w-[60%] flex flex-col items-start justify-center" data-id="element-281">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight max-w-lg" data-id="element-282">
                Jouw bedrijf een boost geven?
              </h2>
              <a href="#contact" className="inline-flex items-center bg-white text-[#007AFF] font-bold py-4 px-8 rounded-full hover:bg-gray-50 transition-colors shadow-md" data-id="element-283">
                Neem contact met ons op{' '}
                <ChevronRightIcon className="ml-2 w-5 h-5" data-id="element-284" />
              </a>
            </div>

            {/* Image Right */}
            <div className="relative z-10 w-full md:w-[50%] h-64 md:h-auto md:absolute md:right-0 md:top-0 md:bottom-0" data-id="element-285">
              <div className="absolute inset-0 bg-gradient-to-r from-[#007AFF] to-transparent z-10 hidden md:block w-32" data-id="element-286"></div>
              <img src="https://cdn.magicpatterns.com/uploads/kPiXVykaBQH5ZZ9eYcbZFN/image.png" alt="Team overleg Noord Digital" className="absolute inset-0 w-full h-full object-cover object-center" style={{
              clipPath: 'polygon(15% 0, 100% 0, 100% 100%, 0 100%)'
            }} data-id="element-287" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] pt-48 pb-12" data-id="element-288">
        {/* Top row — Logo + tagline + socials */}
        <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 mb-16 pb-12 border-b border-gray-100" data-id="element-289">
          <div className="flex items-start gap-4 max-w-md" data-id="element-290">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/mbRatHaTnCE2T37GsGP7pj/Norden_portret_300dpi.png" alt="Noord Digital logo" className="w-14 h-14 object-contain flex-shrink-0 -mt-1" data-id="element-291" />
            <div data-id="element-292">
              <h3 className="text-2xl font-bold tracking-tight mb-1 text-[#111827]" data-id="element-293">
                Noord Digital
              </h3>
              <p className="text-[#004AC7] font-semibold text-sm mb-2" data-id="element-294">
                Online groei op basis van bewijs.
              </p>
              <p className="text-[#6B7280] text-sm leading-relaxed" data-id="element-295">
                Noord Digital helpt B2B-bedrijven groeien buiten hun bestaande
                netwerk, met data als fundament en niet als bijzaak.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 md:pt-1" data-id="element-296">
            {socialLinks.map(social => <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" aria-label={social.label} className="w-10 h-10 rounded-full bg-[#F7F8FA] flex items-center justify-center text-[#6B7280] hover:bg-[#004AC7] hover:text-white transition-all duration-200" data-id="element-297">
                <social.icon className="w-4 h-4" data-id="element-298" />
              </a>)}
          </div>
        </motion.div>

        {/* Navigation columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-16" data-id="element-299">
          {/* CorePrint */}
          <motion.div {...fadeUp} transition={{
          delay: 0.05
        }} data-id="element-300">
            <div className="flex items-center gap-2 mb-5" data-id="element-301">
              <DatabaseIcon className="w-4 h-4 text-[#004AC7]" data-id="element-302" />
              <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-303">
                {footerLinks.coreprint.title}
              </h4>
            </div>
            <ul className="space-y-3" data-id="element-304">
              {footerLinks.coreprint.links.map(link => <li key={link.label} data-id="element-305">
                  <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-306">
                    <ChevronRightIcon className="w-3 h-3 text-gray-300" data-id="element-307" />
                    {link.label}
                  </a>
                </li>)}
            </ul>
          </motion.div>

          {/* Diensten */}
          <motion.div {...fadeUp} transition={{
          delay: 0.1
        }} data-id="element-308">
            <div className="flex items-center gap-2 mb-5" data-id="element-309">
              <SettingsIcon className="w-4 h-4 text-[#004AC7]" data-id="element-310" />
              <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-311">
                {footerLinks.diensten.title}
              </h4>
            </div>
            <ul className="space-y-3" data-id="element-312">
              {footerLinks.diensten.links.map(link => <li key={link.label} data-id="element-313">
                  <a href={link.href} className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-314">
                    <ChevronRightIcon className="w-3 h-3 text-gray-300" data-id="element-315" />
                    {link.label}
                  </a>
                </li>)}
              {footerLinks.diensten.subtle && <li className="pt-1" data-id="element-316">
                  <a href={footerLinks.diensten.subtle.href} target="_blank" rel="noopener noreferrer" className="text-[#9CA3AF] hover:text-[#004AC7] text-xs transition-colors duration-200 flex items-center gap-2" data-id="element-317">
                    <ChevronRightIcon className="w-3 h-3 text-gray-200" data-id="element-318" />
                    {footerLinks.diensten.subtle.label}
                  </a>
                </li>}
            </ul>
          </motion.div>

          {/* Noord Digital */}
          <motion.div {...fadeUp} transition={{
          delay: 0.15
        }} data-id="element-319">
            <div className="flex items-center gap-2 mb-5" data-id="element-320">
              <BuildingIcon className="w-4 h-4 text-[#004AC7]" data-id="element-321" />
              <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-322">
                {footerLinks.noordDigital.title}
              </h4>
            </div>
            <ul className="space-y-3" data-id="element-323">
              {footerLinks.noordDigital.links.map(link => <li key={link.label} data-id="element-324">
                  <a href={link.href} className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-325">
                    <ChevronRightIcon className="w-3 h-3 text-gray-300" data-id="element-326" />
                    {link.label}
                  </a>
                </li>)}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div {...fadeUp} transition={{
          delay: 0.2
        }} data-id="element-327">
            <div className="flex items-center gap-2 mb-5" data-id="element-328">
              <MessageCircleIcon className="w-4 h-4 text-[#004AC7]" data-id="element-329" />
              <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-330">
                {footerLinks.contact.title}
              </h4>
            </div>
            <ul className="space-y-3" data-id="element-331">
              <li data-id="element-332">
                <a href="/#contact" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-333">
                  <CalendarIcon className="w-3.5 h-3.5 text-[#004AC7]" data-id="element-334" />
                  Neem contact op
                </a>
              </li>
              <li data-id="element-335">
                <a href="/#contact" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-336">
                  <CalendarIcon className="w-3.5 h-3.5 text-[#004AC7]" data-id="element-337" />
                  Kennismaking plannen
                </a>
              </li>
              <li data-id="element-338">
                <a href="https://maps.google.com/?q=Schuit+8a+1724+BD+Oudkarspel" target="_blank" rel="noopener noreferrer" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-start gap-2" data-id="element-339">
                  <MapPinIcon className="w-3.5 h-3.5 text-[#004AC7] mt-0.5 flex-shrink-0" data-id="element-340" />
                  <span data-id="element-341">
                    Schuit 8a, 1724 BD Oudkarspel
                    <span className="block text-xs text-[#9CA3AF]" data-id="element-342">
                      Bedrijvenpark Breekland
                    </span>
                  </span>
                </a>
              </li>
              <li data-id="element-343">
                <a href="tel:+31653689686" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-344">
                  <PhoneIcon className="w-3.5 h-3.5 text-[#004AC7]" data-id="element-345" />
                  06 53689686
                </a>
              </li>
              <li data-id="element-346">
                <a href="mailto:info@noorddigital.nl" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-347">
                  <MailIcon className="w-3.5 h-3.5 text-[#004AC7]" data-id="element-348" />
                  info@noorddigital.nl
                </a>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <motion.div {...fadeUp} transition={{
        delay: 0.25
      }} className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-349">
          <div className="flex items-center gap-3" data-id="element-350">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/mbRatHaTnCE2T37GsGP7pj/Norden_portret_300dpi.png" alt="Noord Digital" className="w-8 h-8 object-contain" data-id="element-351" />
            <p className="text-[#6B7280] text-sm" data-id="element-352">
              © 2026 Noord Digital. Alle rechten voorbehouden.
            </p>
          </div>
          <div className="flex items-center gap-4" data-id="element-353">
            <a href="/privacy" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-354">
              Privacy Policy
            </a>
            <span className="text-gray-300" data-id="element-355">·</span>
            <a href="/algemene-voorwaarden" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-356">
              Algemene Voorwaarden
            </a>
            <span className="text-gray-300" data-id="element-357">·</span>
            <a href="/cookiebeleid" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-358">
              Cookiebeleid
            </a>
            <span className="text-gray-300" data-id="element-359">·</span>
            <button type="button" onClick={() => {
            window.dispatchEvent(new CustomEvent('nd-open-cookie-settings'));
          }} className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-360">
              Cookie-instellingen
            </button>
          </div>
        </motion.div>
      </div>
    </footer>;
}